import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import {
  buildContainerStandingsPure,
  resolveAllUserContainers,
  withParticipants,
  isActiveBeneficiary,
  restrictToBeneficiaries,
  toJoinedAtMillis,
  type ContainerStandingRow,
  type GroupMemberRecord,
  type SupportGroupRecord,
  type UserContainerResolution,
} from '@/lib/competitions/competitionRanking';
import { isLogInCompetitionWindow, type CompetitionWindow } from '@/lib/competitions/competitionDates';
import type { InsightLog } from '@/lib/competitions/competitionInsights';

// One read of everything a competition's standings need, ranked with the rule
// shared with mobile and the server (namat#29/#32): every beneficiary of the
// organization takes part, and groups/sections rank by completions per member.

const IN_QUERY_CHUNK = 30;

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export interface IndividualStanding {
  userId: string;
  completed: number;
  rank: number;
}

export interface CompetitionStandings {
  individuals: IndividualStanding[];
  groups: ContainerStandingRow[];
  sections: ContainerStandingRow[];
  groupOf: Map<string, string | null>;
  sectionOf: Map<string, string | null>;
  /** Each participant's (and the viewer's) resolved group/section: for «مجموعتي» (namat#36). */
  resolutions: Map<string, UserContainerResolution>;
  /** The organization's support groups and sections, by id. */
  groupsById: Map<string, SupportGroupRecord>;
  /** Every completed log of the competition, unfiltered: for competitionInsights (namat#35). */
  logs: InsightLog[];
  /** Names of every participant, from the accounts already read for the beneficiary rule. */
  participantUsers: Map<string, StandingUser>;
}

export interface StandingUser {
  firstName: string;
  lastName: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  photoURL: string;
}

export async function loadCompetitionStandings(
  competitionId: string,
  organizationId: string,
  currentUserId: string,
  /** The competition's current dates: only logs inside them count (namat#34). */
  window: CompetitionWindow
): Promise<CompetitionStandings> {
  const [logsSnap, membersSnap] = await Promise.all([
    getDocs(query(collection(db, 'competitionsLogs'), where('competitionId', '==', competitionId), where('completed', '==', true))),
    getDocs(query(collection(db, 'organizationMembers'), where('organizationId', '==', organizationId))),
  ]);

  const counts = new Map<string, number>();
  const logs: InsightLog[] = [];
  logsSnap.docs.forEach((d) => {
    const { userId: uid, habitId, date, completed } = d.data() as InsightLog;
    logs.push({ userId: uid, habitId, date, completed });
    if (uid && isLogInCompetitionWindow(date, window)) counts.set(uid, (counts.get(uid) || 0) + 1);
  });
  const participantIds = membersSnap.docs
    .map((d) => d.data() as { userId?: string; role?: string })
    .filter((m) => m.role === 'customer' && !!m.userId)
    .map((m) => m.userId as string);
  // Only existing accounts with an active beneficiary role take part (namat#87).
  const loggers = Array.from(counts.entries()).map(([userId, completed]) => ({ userId, completed }));
  const accounts = await loadUserDocs([...loggers.map((r) => r.userId), ...participantIds]);
  const restricted = restrictToBeneficiaries(loggers, participantIds, (id) => isActiveBeneficiary(accounts.get(id)));
  const perUser = withParticipants(restricted.perUser, restricted.participantIds);
  const participantUsers = new Map<string, StandingUser>();
  perUser.forEach((r) => {
    const account = accounts.get(r.userId);
    if (account) participantUsers.set(r.userId, toStandingUser(account as Record<string, string | undefined>));
  });

  const { resolutions, groupsById } = await loadContainerResolutions(
    organizationId,
    [...perUser.map((r) => r.userId), currentUserId]
  );
  const groupOf = new Map(Array.from(resolutions.entries()).map(([uid, r]) => [uid, r.groupId]));
  const sectionOf = new Map(Array.from(resolutions.entries()).map(([uid, r]) => [uid, r.sectionId]));

  const all = Array.from(groupsById.values());
  const groups = all.filter((g) => g.groupType !== 'parent').map((g) => ({ id: g.id, name: g.name || 'Unknown' }));
  const sections = all.filter((g) => g.groupType === 'parent').map((g) => ({ id: g.id, name: g.name || 'Unknown' }));

  return {
    individuals: perUser.map((r, i) => ({ ...r, rank: i + 1 })),
    groups: buildContainerStandingsPure(currentUserId, Infinity, groups, groupOf, perUser).rows,
    sections: buildContainerStandingsPure(currentUserId, Infinity, sections, sectionOf, perUser).rows,
    groupOf,
    sectionOf,
    resolutions,
    groupsById,
    logs,
    participantUsers,
  };
}

/**
 * Each user's group and section in this organization, resolved with the rule
 * shared with mobile and the server (resolveAllUserContainers), plus the
 * organization's groups. One supportGroups read and batched groupMembers reads.
 */
export async function loadContainerResolutions(
  organizationId: string,
  userIds: string[]
): Promise<{ resolutions: Map<string, UserContainerResolution>; groupsById: Map<string, SupportGroupRecord> }> {
  const ids = Array.from(new Set(userIds)).filter(Boolean);
  const memberRows: GroupMemberRecord[] = [];
  const [groupsSnap] = await Promise.all([
    getDocs(query(collection(db, 'supportGroups'), where('organization', '==', organizationId))),
    ...chunk(ids, IN_QUERY_CHUNK).map(async (part) => {
      const snap = await getDocs(query(collection(db, 'groupMembers'), where('userId', 'in', part)));
      snap.docs.forEach((d) => {
        const m = d.data();
        if (m.userId && m.groupId) memberRows.push({ userId: m.userId, groupId: m.groupId, joinedAtMs: toJoinedAtMillis(m.joinedAt) });
      });
    }),
  ]);
  const groupsById = new Map<string, SupportGroupRecord>();
  groupsSnap.docs.forEach((d) => {
    const g = d.data();
    groupsById.set(d.id, { id: d.id, organization: g.organization, groupType: g.groupType, parentGroupId: g.parentGroupId, name: g.name });
  });
  return { resolutions: resolveAllUserContainers(ids, organizationId, memberRows, groupsById), groupsById };
}

/** A participant's display name in the viewer's language — names only, never contact details. */
export function standingUserName(u: StandingUser | undefined, language: string): string {
  if (!u) return '';
  return (language === 'ar'
    ? `${u.firstNameAr || u.firstName} ${u.lastNameAr || u.lastName}`
    : `${u.firstNameEn || u.firstName} ${u.lastNameEn || u.lastName}`).trim();
}

async function loadUserDocs(userIds: string[]): Promise<Map<string, unknown>> {
  const out = new Map<string, unknown>();
  const ids = Array.from(new Set(userIds)).filter(Boolean);
  await Promise.all(
    chunk(ids, IN_QUERY_CHUNK).map(async (part) => {
      const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', part)));
      snap.docs.forEach((d) => out.set(d.id, d.data()));
    })
  );
  return out;
}

/** Names and photos for the few users a widget actually shows. */
export async function loadStandingUsers(userIds: string[]): Promise<Map<string, StandingUser>> {
  const out = new Map<string, StandingUser>();
  const ids = Array.from(new Set(userIds)).filter(Boolean);
  await Promise.all(
    chunk(ids, IN_QUERY_CHUNK).map(async (part) => {
      const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', part)));
      snap.docs.forEach((d) => out.set(d.id, toStandingUser(d.data())));
    })
  );
  return out;
}

function toStandingUser(u: Record<string, string | undefined>): StandingUser {
  return {
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    firstNameEn: u.firstNameEn || u.firstName || '',
    lastNameEn: u.lastNameEn || u.lastName || '',
    firstNameAr: u.firstNameAr || u.firstName || '',
    lastNameAr: u.lastNameAr || u.lastName || '',
    photoURL: u.photoURL || '',
  };
}
